import React, { type ReactNode } from "react";

/**
 * The little Markdown the bot uses in its replies: **bold**, *italic* /
 * _italic_, `code`, [label](url) and bare URLs. Lists and paragraphs are
 * plain lines (the bubble keeps them with `whitespace-pre-line`). Rendered
 * as React nodes, never as HTML, so nothing in a message can inject markup.
 */
const TOKEN =
  /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>)]+|(?<![\w*])\*[^*\n]+\*(?![\w*])|(?<![\w_])_[^_\n]+_(?![\w_]))/g;

const link = (href: string, label: string, key: number): ReactNode => (
  <a
    key={key}
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className="font-semibold underline underline-offset-2"
  >
    {label}
  </a>
);

export const renderChatMarkdown = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    if (index > last) nodes.push(text.slice(last, index));
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-black/10 px-1 font-mono text-[0.9em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("[")) {
      const close = token.indexOf("](");
      nodes.push(
        link(token.slice(close + 2, -1), token.slice(1, close), key++),
      );
    } else if (token.startsWith("http")) {
      // A trailing period or comma belongs to the sentence, not the URL.
      const trimmed = token.replace(/[.,;:!?]+$/, "");
      nodes.push(link(trimmed, trimmed, key++));
      if (trimmed.length < token.length)
        nodes.push(token.slice(trimmed.length));
    } else {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

/** The same message as plain text, for previews. */
export const stripChatMarkdown = (text: string): string =>
  text
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1")
    .replace(/(?<![\w*])\*([^*\n]+)\*(?![\w*])/g, "$1")
    .replace(/(?<![\w_])_([^_\n]+)_(?![\w_])/g, "$1");
